// @givmo/app-admin — the employer gift-card confirm→send→success dialog (EP-WEB4). ★ MONEY-MOVING ★
//
// A company sends a company-funded gift card to a roster employee via `POST /admin/company/gift-cards`.
// Confirming CHARGES a company card or DEBITS the company wallet. This is where every money-safety
// invariant is enforced (mirrors <DirectGiveDialog>):
//
//   1. IDEMPOTENCY — a stable key is minted ONCE per attempt-series and REUSED for any retry of the
//      SAME gift. The holder is PAGE-OWNED (passed in as `series`) and keyed by the full gift
//      signature (recipient + amount + funding), so the series SURVIVES a close/reopen. The SAME key
//      rides BOTH the `Idempotency-Key` header AND the body `purchase_request_id` (the gift-card wire
//      carries it in both), so a same-key retry NEVER double-charges — the backend replays.
//   2. NO BLIND RETRY of a MAYBE-CHARGED error — 408/5xx/transport/unconfirmed is RETRYABLE (the charge
//      may have gone through): surface an explicit check-status/try-again state and re-POST under the
//      SAME key only on the user's say-so. Dismissal of the maybe-charged state is ack-gated (Esc /
//      overlay-click / Cancel arm an explicit acknowledgement; only its button closes).
//   3. VELOCITY CAP (429) + INSUFFICIENT FUNDS (409) are FIRST-CLASS, DEFINITE-no-charge states for
//      THIS attempt — each a clear NON-retryable message with the key DROPPED (a genuine later retry
//      starts a fresh series). 429 is diverted here BEFORE the shared retryable classifier (which
//      treats 429 as retryable) — the employer cap rejects pre-charge. BUT if an EARLIER attempt in the
//      series was maybe-charged (retryable), that attempt is unconfirmed: we swap "nothing was charged"
//      for a check-your-sent-gift-cards warning and keep the dismissal ack-gated (`maybeChargedEarlier`).
//      ⚠ BACKEND COUPLING: dropping the key on a 409/429 is only SAFE because of backend invariants
//      invisible from here — the idempotency marker is claimed pre-charge, a completed 4xx is cached +
//      replayed under the same key, and a same-key retry replays to the ORIGINAL outcome (never a second
//      charge). If the backend idempotency semantics change (marker lifetime, 4xx caching, replay), the
//      key-clear decisions in this dialog's catch MUST be re-audited.
//   4. SINGLE-FLIGHT — Send disables + spins while a send is in flight; a double-click cannot fire a
//      second POST (an in-flight ref AND the disabled button) nor mint a second key.
//   5. CONFIRM-BEFORE-CHARGE — the dialog restates recipient + amount + funding + the HONEST
//      itemization before any charge; the gift is FIXED for the dialog's open-session (props).
//   6. THE SIGNED NOTICE — the company's own notice renders in the terms panel directly above the
//      send button (GC-FU12b, `gift-disclaimer.ts`): Notice A on the company wallet, Notice C on a
//      company card, server-driven from its LIVE registry row with its signed floor as the fallback —
//      never the individual purchaser's v2.
//
// THE LAYOUT IS THE APPROVED CHARGE DIALOG'S (CAM-R60, D-CAM-41 → D-CAM-44): the gift is the focal
// point — its value, who receives it, then one card holding where the money comes from and the
// itemization — and the terms sit under their "Terms" heading (D-CAM-97) after any error, adjacent to
// the button, at the Give dialog's width.
//
// THE SERVER'S PRICE (GC-FU31; signed Terms V6 clause 5: processing "itemized for you before you
// pay"): the COMPANY_CARD path is charged the GROSS — the face plus a processor gross-up the company
// always covers (D-GC-2), e.g. $50 → $51.78, priced by the stored card's brand — so the confirm states
// the exact card processing and total from the server's quote (`gift-card-quote.ts`), never a figure
// worked out here, and the send carries them (`expected_amount_charged` / `expected_fee`): a backend
// that would charge others refuses with `409 CHARGE_QUOTE_CHANGED` before money moves, and the dialog
// re-reads the price and asks again under a new key. Send is held while the quote loads or has failed,
// and a failed read is retried in the dialog, never by closing it (a close resets the composed gift).
// The stated price is frozen with the key (the page-owned series): "Try again", or the same gift
// re-entered after a maybe-charged close, resends EXACTLY what it stated. The COMPANY_WALLET path
// debits EXACTLY face (no fee line, no quote). Post-send, the success state surfaces the exact
// `amount_charged` gross FROM THE RESPONSE (GC1-CMP1).
//
// RECEIPT COPY forks by funding source on success (plan §3 — LAW): company_card ⇒ one neutral line
// ("A tax receipt will be emailed for this purchase."); company_wallet ⇒ receipt-LESS (the deposit was
// already receipted) — NO receipt mention. NEVER any recipient-receipt copy.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  sdk,
  firstErrorCode,
  idempotencyKey,
  isGivmoError,
  supportReference,
  GivmoError,
  type CompanyGiftCardOut,
} from '@givmo/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, Icon, testIdProp } from '@givmo/design-system';
import { AlertTriangle, CheckCircle2, Gauge, Wallet as WalletIcon } from 'lucide-react';
import { isRetryableChargeError, maybeChargedLine } from '@givmo/payments';
import { CHARGE_QUOTE_CHANGED } from '../../components/charge-quote';
import {
  fetchGiftCardQuote,
  giftCardQuoteQueryKey,
  statedGiftCardCharge,
  toGiftCardQuote,
} from './gift-card-quote';
import {
  buildCompanyGiftCardBody,
  formatGiftCardCharged,
  formatGiftCardDollars,
  giftCardReceiptCopy,
  isInsufficientFundsError,
  isVelocityCapError,
  type Funding,
  type GiftCardRecipient,
} from './gift-cards-decode';
import type {
  GiftCardChargeSeries,
  GiftCardHeldRequest,
  GiftCardSignature,
} from './gift-card-idempotency';
import { ChargeSummary } from '../../components/charge-summary';
import { GiftDisclosureBlock } from './gift-disclosure-block';
import { companyGiftCardNotice } from './gift-disclaimer';
import { useCompanyGiftCardNotice } from './use-company-gift-card-notice';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { useRevealOnShow } from '../../components/reveal-on-show';

const PREFIX = 'gift-card';

export interface SendGiftCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The resolved recipient roster employee (name + work_email). */
  recipient: GiftCardRecipient;
  /** Whole-dollar integer amount (validated in [$15, $1,000] by the caller). */
  amount: number;
  /** The chosen funding source: a specific company card, or the company wallet. */
  funding: Funding;
  /** Human-readable funding label restated in the confirm copy (e.g. "your Visa Card •••• 4242"). */
  fundingLabel: string;
  /**
   * The funding source as the summary's "From" row names it ("Visa Card •••• 4242", "Company
   * wallet ($1,250.00 available)" — `fundingSummaryLabel`), as the Give dialog takes it. Absent →
   * the row shows `fundingLabel`.
   */
  fundingSummary?: string;
  /** The optional gift display name to send (`recipient_name`), or null. */
  recipientName: string | null;
  /** The optional gift message to send (`recipient_message`), or null. */
  recipientMessage: string | null;
  /**
   * The PAGE-OWNED charge series (one per page session, held in a page `useRef`): the idempotency key
   * and the request it was minted for — on a card, the price it was stated at — so a maybe-charged
   * send survives this dialog's unmount and is resent exactly as it went out.
   */
  series: GiftCardChargeSeries<GiftCardSignature>;
  /** Fired ONCE when the gift confirms — the page invalidates the sent-list + wallet reads. */
  onSuccess: () => void;
  /** Fired when the dialog closes FROM the success state — the page toasts + resets the form. */
  onCloseAfterSuccess: () => void;
  /** Fired when the dialog closes after a NON-success send attempt — the page refreshes its reads. */
  onCloseAfterAttempt: () => void;
}

/** The send state machine. `retryable-error` REUSES the idempotency key on the next attempt. The two
 *  first-class terminal states carry `maybeChargedEarlier` — true when a PRIOR attempt in this series
 *  hit a maybe-charged (retryable) error, so we must not claim "nothing was charged" and the dismissal
 *  stays acknowledgement-gated (see the catch). */
type Phase =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'retryable-error'; message: string; reference: string | null }
  | { status: 'insufficient-funds'; message: string; maybeChargedEarlier: boolean }
  | { status: 'velocity-cap'; message: string; maybeChargedEarlier: boolean }
  | { status: 'quote-changed'; maybeChargedEarlier: boolean }
  | { status: 'fatal-error'; message: string; reference: string | null }
  | { status: 'success'; card: CompanyGiftCardOut };

const RETRYABLE_HINT =
  'We could not confirm whether the gift went through. Do not assume it failed — check your sent ' +
  'gift cards, or try again. A retry is de-duplicated and will not charge twice.';

// The shortfall itself; what follows it — who adds funds, and where — is the host's
// (`words.giftCardAddFundsRetry`, EP-MNT5(h)).
const INSUFFICIENT_FUNDS_LEAD =
  'Your company wallet does not have enough available balance for this gift.';

const VELOCITY_CAP_REASON =
  'You’ve reached your company’s gift-card sending limit for now. Please try again later.';

// A FRESH (non-retry) arrival at 409/429 definitely did not charge. But if an EARLIER attempt in this
// series was maybe-charged (a retryable error), that attempt is unconfirmed — the narrow orphan window
// where the charge committed externally but the DB write failed → 5xx released the idempotency marker →
// this retry lands on the cap/insufficient branch. So swap the definite claim for a check-first framing.
const NOTHING_CHARGED_TAIL = 'Nothing was charged.';
const COULD_NOT_CONFIRM_EARLIER_TAIL =
  'We couldn’t confirm your earlier attempt — check your sent gift cards before retrying.';

const CLOSE_ACK_BODY =
  'The charge may have gone through even though we could not confirm it. After closing, check your ' +
  'recent gift cards before sending again — re-sending the exact same gift (same recipient, amount, ' +
  'and payment method) is safe and will not double-charge, but a different amount would be a new charge.';

// GC-FU31: what a card send says when it cannot read the server's price (Send held, no figure), with
// the price check retried IN the dialog: closing resets the gift the admin composed (review M1). The
// "Try again" after the sentence is the control.
const QUOTE_FAILED =
  'We couldn’t check the card processing for this gift card, so it can’t be sent yet.';
const QUOTE_RETRY = 'Try again';
// Both halves: the gross is the face plus the processing, so a move in either is a new price. After
// an attempt that may have charged, the check-first tail takes the place of "nothing was charged".
const QUOTE_CHANGED =
  'The price of this gift card changed — its card processing or its total — so nothing was charged. ' +
  'Check the new figures, then send again.';
const QUOTE_CHANGED_LEAD =
  'The price of this gift card changed — its card processing or its total.';

export function SendGiftCardDialog({
  open,
  onOpenChange,
  recipient,
  amount,
  funding,
  fundingLabel,
  fundingSummary,
  recipientName,
  recipientMessage,
  series,
  onSuccess,
  onCloseAfterSuccess,
  onCloseAfterAttempt,
}: SendGiftCardDialogProps) {
  // Who adds funds, and where, is this host's (EP-MNT5(h)) — words only; the charge path is untouched.
  const { words } = useEmployerModulePaths();
  const [phase, setPhase] = useState<Phase>({ status: 'idle' });
  const [closeAck, setCloseAck] = useState(false);

  const inFlight = useRef(false);
  const attempted = useRef(false);
  // Whether ANY attempt in this open-session hit a maybe-charged (retryable) error — drives the
  // "couldn't confirm your earlier attempt" framing + the ack gate on a later terminal 409/429.
  const sawRetryable = useRef(false);

  const queryClient = useQueryClient();

  // The signed notice for this funding source (GC-FU12b): Notice A on the wallet, Notice C on a card —
  // its LIVE row while the confirm is open, its floor (the same signed bytes) until then or on failure.
  const notice = companyGiftCardNotice(funding);
  const shownNotice = useCompanyGiftCardNotice(notice, open);

  // The server's price for a CARD send (GC-FU31): one card at this face on this card. `staleTime: 0` —
  // a price is read fresh each time it is stated (the charge's guard backs it anyway). None on the
  // wallet, which debits exactly face.
  const cardId = funding.kind === 'card' ? funding.cardId : null;
  const quoteKey = giftCardQuoteQueryKey(amount, 1, cardId ?? '');
  const quoteQuery = useQuery({
    queryKey: quoteKey,
    queryFn: () => fetchGiftCardQuote(amount, 1, cardId ?? ''),
    enabled: open && cardId !== null,
    select: (body: unknown) => toGiftCardQuote(body, { amount, count: 1 }),
    staleTime: 0,
  });
  // A failed read being retried in place is "loading" again: no figure, Send spinning.
  const quoteStatus: 'none' | 'loading' | 'failed' | 'ready' =
    cardId === null
      ? 'none'
      : quoteQuery.isPending
        ? 'loading'
        : quoteQuery.isError || quoteQuery.data == null
          ? quoteQuery.isFetching
            ? 'loading'
            : 'failed'
          : 'ready';
  const liveQuote = quoteStatus === 'ready' ? (quoteQuery.data ?? null) : null;

  const signature = useMemo<GiftCardSignature>(
    () => ({ recipientWorkEmail: recipient.workEmail, amount, funding }),
    [recipient.workEmail, amount, funding],
  );

  // Reset ONLY on a genuine CLOSED→OPEN transition. ★ The idempotency holder is DELIBERATELY NOT
  // cleared here (nor on unmount — it is page-owned): persisting it across close/reopen keeps a
  // maybe-charged gift's series alive (same gift → same key → the backend replays, never re-charges).
  // A request still held at open is an attempt this series never confirmed — it is held from its send
  // until a success or a definite no-charge, and the dialog cannot close mid-flight — so a definite
  // refusal of the resend keeps the check-first framing and the ack gate (constraint 3, review L2).
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setPhase({ status: 'idle' });
      setCloseAck(false);
      attempted.current = false;
      inFlight.current = false;
      sawRetryable.current = series.heldFor(signature) !== null;
    }
    prevOpen.current = open;
  }, [open, series, signature]);

  const busy = phase.status === 'sending';

  // The request held for this gift (in flight, in doubt, or re-entered after a maybe-charged close):
  // while one is held the dialog states the price it will RESEND, never a live one that moved since.
  const heldRequest = series.heldFor(signature);
  const shownQuote = heldRequest?.quote ?? liveQuote;

  const finalizeClose = useCallback(() => {
    if (phase.status === 'success') {
      onCloseAfterSuccess();
    } else if (attempted.current) {
      onCloseAfterAttempt();
    }
    onOpenChange(false);
  }, [phase.status, onCloseAfterSuccess, onCloseAfterAttempt, onOpenChange]);

  // Whether dismissal must route through the explicit acknowledgement step: a maybe-charged
  // (`retryable-error`) state, OR a terminal 409/429 REACHED FROM a prior maybe-charged attempt (the
  // earlier attempt is unconfirmed, so a silent close would hide a possible charge).
  const dismissNeedsAck =
    phase.status === 'retryable-error' ||
    ((phase.status === 'velocity-cap' ||
      phase.status === 'insufficient-funds' ||
      phase.status === 'quote-changed') &&
      phase.maybeChargedEarlier);
  // The acknowledgement replaces the footer, so on a phone it opens below the fold: it is brought into
  // view (P5).
  const closeAckRef = useRevealOnShow<HTMLDivElement>(dismissNeedsAck && closeAck);

  // Dismissal gate: never close mid-flight; an ack-required dismissal ALWAYS routes through the
  // explicit acknowledgement step; everything else closes normally.
  const requestClose = useCallback(() => {
    if (busy) return;
    if (dismissNeedsAck) {
      setCloseAck(true);
      return;
    }
    finalizeClose();
  }, [busy, dismissNeedsAck, finalizeClose]);

  const runSend = useCallback(async () => {
    if (inFlight.current) return; // single-flight: a second click is a no-op.

    // The request this send makes: the one held for this gift — "Try again", or the same gift
    // re-entered after a close — exactly as it was stated; else a new one, stated from the price the
    // dialog shows now. A card send never goes out without that price (the control is held too).
    let request: GiftCardHeldRequest<GiftCardSignature> | null = series.heldFor(signature);
    if (request === null) {
      if (funding.kind === 'card' && liveQuote === null) return;
      request = { signature, quote: funding.kind === 'card' ? liveQuote : null };
    }
    inFlight.current = true;
    attempted.current = true;

    // Mint (or REUSE, on a retry of this SAME gift) the stable key BEFORE the request, held with the
    // request it is for. The SAME value is the `Idempotency-Key` HEADER and the body
    // `purchase_request_id` (both carriers); a held request resends its stated price unchanged.
    const key = series.keyFor(request);
    const body = buildCompanyGiftCardBody({
      amount,
      funding,
      recipientWorkEmail: recipient.workEmail,
      recipientName,
      recipientMessage,
      purchaseRequestId: key,
      stated: request.quote === null ? null : statedGiftCardCharge(request.quote),
    });

    try {
      setPhase({ status: 'sending' });
      const { data } = await sdk.companiesSendCompanyGiftCard({
        body,
        ...idempotencyKey(key),
        throwOnError: true,
      });

      // Confirmed: DROP the key (a brand-new gift gets a brand-new series), release the flight lock,
      // hand success to the page (invalidate sent-list + wallet), and show the forked receipt copy +
      // the exact charged gross from the response (EP-FIX3, card path only).
      series.clear();
      inFlight.current = false;
      onSuccess();
      setPhase({ status: 'success', card: data });
    } catch (caught: unknown) {
      inFlight.current = false;
      const err = isGivmoError(caught)
        ? caught
        : new GivmoError({ message: 'Something went wrong. Please try again.', kind: 'unknown' });

      // VELOCITY CAP (429) — this attempt is a DEFINITE pre-charge no-charge; its own first-class
      // NON-retryable state, checked BEFORE the shared retryable classifier (which treats 429 as
      // retryable). Drop the key. BUT if an earlier attempt was maybe-charged, that one is unconfirmed —
      // don't claim "nothing was charged", and keep the dismissal ack-gated.
      if (isVelocityCapError(err)) {
        series.clear();
        const maybeChargedEarlier = sawRetryable.current;
        setPhase({
          status: 'velocity-cap',
          message: `${VELOCITY_CAP_REASON} ${
            maybeChargedEarlier ? COULD_NOT_CONFIRM_EARLIER_TAIL : NOTHING_CHARGED_TAIL
          }`,
          maybeChargedEarlier,
        });
        return;
      }

      // INSUFFICIENT FUNDS (409) — this attempt is a definite no-charge, its own first-class state. Drop
      // the key. Same earlier-attempt caveat as the velocity cap above.
      if (isInsufficientFundsError(err)) {
        series.clear();
        const maybeChargedEarlier = sawRetryable.current;
        setPhase({
          status: 'insufficient-funds',
          message: `${INSUFFICIENT_FUNDS_LEAD} ${words.giftCardAddFundsRetry} ${
            maybeChargedEarlier ? COULD_NOT_CONFIRM_EARLIER_TAIL : NOTHING_CHARGED_TAIL
          }`,
          maybeChargedEarlier,
        });
        return;
      }

      // PRICE CHANGED (409 CHARGE_QUOTE_CHANGED, GC-FU31) — the stated card processing or total is no
      // longer what the charge would take (a card-brand fee row, a re-rate), refused before any money
      // moved: a definite no-charge for this attempt. Drop the key and read the price again. RESET,
      // not invalidate: an invalidated query keeps its data while it refetches, so the dialog would
      // state the refused figures with Send live; reset empties it, so until the new price lands no
      // figure is stated and Send is held. Same earlier-attempt caveat as the velocity cap above.
      if (firstErrorCode(err) === CHARGE_QUOTE_CHANGED) {
        series.clear();
        void queryClient.resetQueries({ queryKey: quoteKey });
        setPhase({ status: 'quote-changed', maybeChargedEarlier: sawRetryable.current });
        return;
      }

      const reference = supportReference(err);
      // RETRYABLE (maybe-charged — 408/5xx/transport/unconfirmed): KEEP the key so the next attempt
      // re-POSTs the SAME gift and the backend replays it. Explicit try-again — NEVER auto-retry. Mark
      // the series maybe-charged so a later terminal 409/429 warns instead of claiming no charge. Its
      // line is the server's own sentence where the server worded one, never a processor's (EP-WEB34).
      if (isRetryableChargeError(err)) {
        sawRetryable.current = true;
        setPhase({
          status: 'retryable-error',
          message: maybeChargedLine(err, RETRYABLE_HINT),
          reference,
        });
        return;
      }

      // FATAL (definite no-charge — 400/401/403/422/other): drop the key. The backend caches a completed
      // 4xx under the key and REPLAYS it for a same-key retry, so reuse would replay the failure; a
      // fresh attempt starts a fresh series.
      series.clear();
      setPhase({ status: 'fatal-error', message: err.message, reference });
    }
  }, [
    signature,
    amount,
    funding,
    recipient.workEmail,
    recipientName,
    recipientMessage,
    series,
    liveQuote,
    queryClient,
    quoteKey,
    onSuccess,
    words,
  ]);

  const isSuccess = phase.status === 'success';
  const title = isSuccess ? 'Gift card sent' : 'Confirm your gift card';
  const amountLabel = formatGiftCardDollars(amount);

  // Fork the confirm verb + the itemization by funding source. The CARD path is charged the GROSS
  // (face + a processor gross-up, D-GC-2), stated from the server's quote — the held request's price
  // while one is held, else the live quote's — and NEVER worked out here; while there is no price the
  // figures say so and Send is held. The WALLET path debits EXACTLY face, with no fee line.
  const processingFigure =
    shownQuote === null ? null : (formatGiftCardCharged(shownQuote.fee) ?? shownQuote.fee);
  const totalFigure =
    shownQuote === null
      ? null
      : (formatGiftCardCharged(shownQuote.amountCharged) ?? shownQuote.amountCharged);
  const noFigure = quoteStatus === 'failed' ? 'Unavailable' : 'Checking…';
  // No price and none held: the line says why and offers the price check again, in place.
  const quoteFailed = funding.kind === 'card' && quoteStatus === 'failed' && heldRequest === null;
  const confirmVerb =
    funding.kind === 'wallet'
      ? 'This debits your company wallet immediately.'
      : 'This charges your company card immediately.';
  const totalLabel =
    funding.kind === 'wallet' ? 'Debited from wallet today' : 'Total charged today';
  const totalValue = funding.kind === 'wallet' ? amountLabel : (totalFigure ?? noFigure);
  // A card send is armed once its price is in; a held request is resent as it was and needs none.
  const priceHeldBack = funding.kind === 'card' && heldRequest === null && liveQuote === null;

  const receiptLine = giftCardReceiptCopy(funding);

  // Post-send: the exact GROSS charged, surfaced FROM THE RESPONSE (EP-FIX3 / GC1-CMP1). Present only
  // for a COMPANY_CARD send; null for a wallet send (face only) — never recomputed client-side.
  const chargedLabel =
    phase.status === 'success' ? formatGiftCardCharged(phase.card.amount_charged) : null;

  // One type-narrowed view of the current error state (testid + icon + copy + optional support ref).
  const errorView =
    phase.status === 'retryable-error'
      ? {
          testId: `${PREFIX}-retryable-error`,
          icon: AlertTriangle,
          message: phase.message,
          reference: phase.reference,
        }
      : phase.status === 'insufficient-funds'
        ? {
            testId: `${PREFIX}-insufficient-funds`,
            icon: WalletIcon,
            message: phase.message,
            reference: null,
          }
        : phase.status === 'velocity-cap'
          ? {
              testId: `${PREFIX}-velocity-cap`,
              icon: Gauge,
              message: phase.message,
              reference: null,
            }
          : phase.status === 'quote-changed'
            ? {
                testId: `${PREFIX}-quote-changed`,
                icon: AlertTriangle,
                message: phase.maybeChargedEarlier
                  ? `${QUOTE_CHANGED_LEAD} ${COULD_NOT_CONFIRM_EARLIER_TAIL}`
                  : QUOTE_CHANGED,
                reference: null,
              }
            : phase.status === 'fatal-error'
              ? {
                  testId: `${PREFIX}-fatal-error`,
                  icon: AlertTriangle,
                  message: phase.message,
                  reference: phase.reference,
                }
              : null;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          requestClose();
          return;
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label={title}
        className="flex flex-col gap-16"
        width={480}
        testId={`${PREFIX}-confirm-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>

        {isSuccess ? (
          // ── Success: forked receipt copy (CARD ⇒ receipt emailed; WALLET ⇒ no receipt mention). ──
          <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-success`)}>
            <div className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-12">
              <Icon icon={CheckCircle2} size={18} aria-hidden className="mt-1 text-success-600" />
              <div className="flex flex-col gap-4">
                <p className="font-sans text-s14 font-w500 leading-h15 tracking-givmo text-text-strong">
                  You sent a {amountLabel} gift card to {recipient.name}.
                </p>
                {chargedLabel !== null && (
                  <p
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary tabular-nums"
                    {...testIdProp(`${PREFIX}-success-charged`)}
                  >
                    Charged {chargedLabel}, including card processing.
                  </p>
                )}
                {receiptLine !== null && (
                  <p
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    {...testIdProp(`${PREFIX}-success-receipt`)}
                  >
                    {receiptLine}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-8">
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                onClick={finalizeClose}
                testId={`${PREFIX}-done`}
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* ── The gift is the focal point (D-CAM-41): its value, who receives it, then one card
                holding the itemization — the source, (card only) the server's card processing, and
                the funding-forked total: the gross charged on a card, exactly face from the wallet.
                It stays ABOVE the terms. ── */}
            <ChargeSummary
              mark={{ name: recipient.name, logoUrl: null }}
              amount={amountLabel}
              recipient={recipient.name}
              context={`Gift card · ${recipient.workEmail}`}
              rows={[
                {
                  label: 'From',
                  value: fundingSummary ?? fundingLabel,
                  testId: `${PREFIX}-summary-from`,
                },
                ...(funding.kind === 'card'
                  ? [
                      {
                        label: 'Card processing',
                        value: processingFigure ?? noFigure,
                        testId: `${PREFIX}-summary-processing`,
                      },
                    ]
                  : []),
                { label: totalLabel, value: totalValue, testId: `${PREFIX}-total` },
              ]}
              testId={`${PREFIX}-itemization`}
            />

            <Dialog.Description
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${PREFIX}-charge-line`)}
            >
              {quoteFailed ? (
                <>
                  {QUOTE_FAILED}{' '}
                  <button
                    type="button"
                    onClick={() => void quoteQuery.refetch()}
                    className="font-w600 text-brand-600 outline-none hover:underline focus-visible:underline"
                    {...testIdProp(`${PREFIX}-quote-retry`)}
                  >
                    {QUOTE_RETRY}
                  </button>
                </>
              ) : (
                confirmVerb
              )}
            </Dialog.Description>

            {errorView && (
              <div className="flex flex-col items-start gap-2" {...testIdProp(errorView.testId)}>
                <div className="flex items-start gap-8">
                  <Icon
                    icon={errorView.icon}
                    size={16}
                    aria-hidden
                    className="mt-1 text-danger-700"
                  />
                  <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                    {errorView.message}
                  </p>
                </div>
                {errorView.reference !== null && (
                  <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
                    {errorView.reference}
                  </p>
                )}
              </div>
            )}

            {/* ── The company's signed notice for this funding source, directly above the send
                button: after the error, as the Give dialog places its terms (D-CAM-41, "adjacent to
                the button"). ── */}
            <GiftDisclosureBlock notice={notice} shown={shownNotice} />

            {dismissNeedsAck && closeAck ? (
              // ── Maybe-charged close acknowledgement: the footer is replaced so the ONLY ways forward
              //    are an explicit acknowledged close or going back. ──
              <div
                ref={closeAckRef}
                className="flex scroll-mb-24 flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-12"
                {...testIdProp(`${PREFIX}-close-ack`)}
              >
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary">
                  {CLOSE_ACK_BODY}
                </p>
                <div className="flex items-center justify-end gap-8">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    onClick={() => setCloseAck(false)}
                    testId={`${PREFIX}-close-ack-back`}
                  >
                    Go back
                  </Button>
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="md"
                    onClick={finalizeClose}
                    testId={`${PREFIX}-close-ack-confirm`}
                  >
                    I understand — close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-end gap-8">
                <Dialog.Close
                  disabled={busy}
                  render={<Button appearance="saas" tone="secondary" size="md" />}
                >
                  Cancel
                </Dialog.Close>
                <Button
                  appearance="saas"
                  tone="primary"
                  size="md"
                  // Single-flight: disabled + spinning while in flight. Insufficient funds / velocity cap
                  // are terminal (no retry) — the user must change funding or wait, so no Send is offered.
                  // A card send is held (spinning while it loads) until its price is in.
                  disabled={
                    busy ||
                    phase.status === 'insufficient-funds' ||
                    phase.status === 'velocity-cap' ||
                    priceHeldBack
                  }
                  loading={busy || (priceHeldBack && quoteStatus === 'loading')}
                  onClick={() => void runSend()}
                  testId={`${PREFIX}-confirm-submit`}
                >
                  {phase.status === 'retryable-error' ? 'Try again' : `Send ${amountLabel}`}
                </Button>
              </div>
            )}
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
